// ============================================================
// COREBANK — LENDING ENGINE
// ============================================================
// Facility management, drawdowns, repayments, interest accrual,
// IFRS 9 staging, ECL calculation, covenant monitoring.
//
// Depends on: LedgerEngine (injected via constructor)
// The ledger provides all accounting — this engine has NO internal
// ledger, NO MiniLedger, NO shortcut. Every financial event posts
// through the real double-entry engine with full validation.
//
// Used by: integration-tests.js, lending-module.jsx
// ============================================================

import { AccountType, Direction } from './ledger-engine.js';
import { requireFiniteNumber } from '../validate.js';

let _seq = 0;
function genId(pfx = '') {
  _seq++;
  return `${pfx}${Date.now().toString(36)}${_seq.toString(36).padStart(4, '0')}${Math.random().toString(36).slice(2, 6)}`;
}

// ---- Types ----

const FacilityType = { TERM_LOAN: 'TERM_LOAN', REVOLVING_CREDIT: 'REVOLVING_CREDIT', OVERDRAFT: 'OVERDRAFT', INVOICE_DISC: 'INVOICE_DISC' };

const FacilityStatus = { DRAFT: 'DRAFT', APPROVED: 'APPROVED', ACTIVE: 'ACTIVE', EXPIRED: 'EXPIRED', DEFAULTED: 'DEFAULTED', CLOSED: 'CLOSED' };

const FACILITY_TRANSITIONS = {
  [FacilityStatus.DRAFT]: [FacilityStatus.APPROVED, FacilityStatus.CLOSED],
  [FacilityStatus.APPROVED]: [FacilityStatus.ACTIVE, FacilityStatus.CLOSED],
  [FacilityStatus.ACTIVE]: [FacilityStatus.EXPIRED, FacilityStatus.DEFAULTED, FacilityStatus.CLOSED],
  [FacilityStatus.EXPIRED]: [FacilityStatus.CLOSED],
  [FacilityStatus.DEFAULTED]: [FacilityStatus.ACTIVE, FacilityStatus.CLOSED],
  [FacilityStatus.CLOSED]: [],
};

const RepaymentType = { BULLET: 'BULLET', AMORTISING: 'AMORTISING', INTEREST_ONLY: 'INTEREST_ONLY' };
const InterestMethod = { ACT_365: 'ACT_365', ACT_360: 'ACT_360' };
const IFRS9Stage = { STAGE_1: 'STAGE_1', STAGE_2: 'STAGE_2', STAGE_3: 'STAGE_3' };

// ---- Engine ----

class LendingEngine {
  constructor(ledger) {
    if (!ledger) throw new Error('LendingEngine requires a LedgerEngine instance');
    this.ledger = ledger;
    this.facilities = new Map();
    this.drawdowns = [];
    this.repayments = [];
    this.accruals = new Map();
    this.schedules = new Map();
  }

  // ---- Facility Lifecycle ----

  createFacility({ customerId, customerName, type, committedAmount, currency, interestRate, interestBasis, marginOverBase, baseRate, tenor, startDate, maturityDate, repaymentType, repaymentFrequency, covenants, security, metadata }) {
    if (!customerId) throw new Error('customerId is required');
    if (!committedAmount || committedAmount <= 0) throw new Error('Committed amount must be positive');
    if (!type || !Object.values(FacilityType).includes(type)) throw new Error('Valid facility type required');
    if (interestRate !== undefined && interestRate !== null) {
      requireFiniteNumber(interestRate, 'interestRate');
    }

    const facility = {
      id: genId('fac_'),
      customerId,
      customerName: customerName || '',
      type,
      committedAmount,
      drawnAmount: 0,
      currency: currency || 'GBP',
      interestRate: interestRate || null,
      interestBasis: interestBasis || InterestMethod.ACT_365,
      marginOverBase: marginOverBase || null,
      baseRate: baseRate || null,
      effectiveRate: interestRate || (marginOverBase && baseRate ? marginOverBase + baseRate : 0),
      tenor,
      startDate: startDate || new Date().toISOString(),
      maturityDate,
      repaymentType: repaymentType || RepaymentType.INTEREST_ONLY,
      repaymentFrequency: repaymentFrequency || 'MONTHLY',
      covenants: covenants || [],
      security: security || [],
      status: FacilityStatus.DRAFT,
      statusHistory: [{ status: FacilityStatus.DRAFT, at: new Date().toISOString(), by: 'system' }],
      loanAccountId: null,
      ifrs9Stage: IFRS9Stage.STAGE_1,
      ifrs9History: [{ stage: IFRS9Stage.STAGE_1, at: new Date().toISOString(), reason: 'Initial staging' }],
      eclProvision: 0,
      metadata: metadata || {},
      createdAt: new Date().toISOString(),
    };
    this.facilities.set(facility.id, facility);
    this.accruals.set(facility.id, { accruedInterest: 0, entries: [] });
    return facility;
  }

  getFacility(id) { return this.facilities.get(id); }
  getAllFacilities() { return Array.from(this.facilities.values()); }

  approveFacility(facilityId, approvedBy, conditions = []) {
    const f = this._getFacilityOrThrow(facilityId);
    this._transitionFacility(f, FacilityStatus.APPROVED, approvedBy);
    f.approvalConditions = conditions;
    return f;
  }

  activateFacility(facilityId, by) {
    const f = this._getFacilityOrThrow(facilityId);
    if (f.status !== FacilityStatus.APPROVED) throw new Error('Must be APPROVED to activate');

    // Create loan asset account in the REAL ledger
    const loanAccount = this.ledger.createAccount({
      accountNumber: `LOAN-${f.id.slice(-10).toUpperCase()}`,
      name: `Loan: ${f.customerName || f.type} ${f.committedAmount.toLocaleString()}`,
      type: AccountType.ASSET,
      currency: f.currency,
      customerId: f.customerId,
      metadata: { internal: false, facilityId: f.id, facilityType: f.type },
      autoActivate: true,
    });
    f.loanAccountId = loanAccount.id;
    this._transitionFacility(f, FacilityStatus.ACTIVE, by);

    if (f.repaymentType === RepaymentType.AMORTISING && f.maturityDate) {
      this._generateAmortisationSchedule(f);
    }
    return f;
  }

  // ---- Drawdown & Repayment ----
  // These post through the real ledger — full validation, balanced entries.

  drawdown(facilityId, amount, targetAccountId) {
    const f = this._getFacilityOrThrow(facilityId);
    if (f.status !== FacilityStatus.ACTIVE) throw new Error('Facility not active');
    if (!amount || amount <= 0) throw new Error('Amount must be positive');
    // PKB-003: Term loans cannot re-draw after full repayment
    if (f.type === FacilityType.TERM_LOAN) {
      const totalDrawdowns = this.drawdowns
        .filter(d => d.facilityId === facilityId)
        .reduce((sum, d) => sum + d.amount, 0);
      const totalRepayments = this.repayments
        .filter(r => r.facilityId === facilityId)
        .reduce((sum, r) => sum + r.amount, 0);
      // If fully repaid (had drawdowns, repaid in full, drawn is 0), block further drawdowns
      if (totalDrawdowns > 0 && totalRepayments >= totalDrawdowns && f.drawnAmount === 0) {
        throw new Error('Term loan fully repaid — no further drawdowns permitted');
      }
      if (totalDrawdowns + amount > f.committedAmount) {
        throw new Error(`Exceeds facility limit: term loan total drawdowns ${totalDrawdowns} + ${amount} > committed ${f.committedAmount}`);
      }
    } else if (f.drawnAmount + amount > f.committedAmount) {
      throw new Error(`Exceeds facility limit: drawn ${f.drawnAmount} + ${amount} > committed ${f.committedAmount}`);
    }

    // DR Loan Account (asset up), CR Customer Account (liability up — they receive money)
    const txn = this.ledger.postTransaction({
      type: 'LOAN_DRAWDOWN',
      reference: `DD-${f.id.slice(0, 8)}-${Date.now()}`,
      legs: [
        { accountId: f.loanAccountId, amount, direction: Direction.DEBIT, currency: f.currency },
        { accountId: targetAccountId, amount, direction: Direction.CREDIT, currency: f.currency },
      ],
      metadata: { facilityId: f.id, drawdownAmount: amount },
      createdBy: 'lending-engine',
    });

    f.drawnAmount = Math.round((f.drawnAmount + amount) * 100) / 100;
    const record = {
      id: genId('dd_'), facilityId, amount, targetAccountId,
      transactionId: txn.id, date: new Date().toISOString(),
    };
    this.drawdowns.push(record);
    return record;
  }

  repay(facilityId, amount, sourceAccountId) {
    const f = this._getFacilityOrThrow(facilityId);
    if (f.status !== FacilityStatus.ACTIVE) throw new Error('Facility not active');
    if (!amount || amount <= 0) throw new Error('Amount must be positive');
    if (amount > f.drawnAmount) throw new Error(`Repayment ${amount} exceeds outstanding ${f.drawnAmount}`);

    // DR Customer Account (liability down — they pay), CR Loan Account (asset down)
    const txn = this.ledger.postTransaction({
      type: 'LOAN_REPAYMENT',
      reference: `REP-${f.id.slice(0, 8)}-${Date.now()}`,
      legs: [
        { accountId: sourceAccountId, amount, direction: Direction.DEBIT, currency: f.currency },
        { accountId: f.loanAccountId, amount, direction: Direction.CREDIT, currency: f.currency },
      ],
      metadata: { facilityId: f.id, repaymentAmount: amount },
      createdBy: 'lending-engine',
    });

    f.drawnAmount = Math.round((f.drawnAmount - amount) * 100) / 100;
    const record = {
      id: genId('rep_'), facilityId, amount, sourceAccountId,
      transactionId: txn.id, date: new Date().toISOString(),
    };
    this.repayments.push(record);
    return record;
  }

  // ---- Interest Accrual ----

  accrueInterest(facilityId, date) {
    const f = this._getFacilityOrThrow(facilityId);
    if (f.status !== FacilityStatus.ACTIVE || f.drawnAmount <= 0) return null;

    const rate = f.effectiveRate;
    const divisor = f.interestBasis === InterestMethod.ACT_360 ? 360 : 365;
    const dailyInterest = Math.round((f.drawnAmount * rate / divisor) * 100) / 100;
    if (dailyInterest <= 0) return null;

    // DR Loan Interest Receivable (asset), CR Interest Income (income)
    const intReceivable = this.ledger.getAccountByNumber('INT-LOAN-INTEREST-RECEIVABLE');
    const intIncome = this.ledger.getAccountByNumber('INT-INTEREST-INCOME');

    this.ledger.postTransaction({
      type: 'LOAN_INTEREST_ACCRUAL',
      reference: `LINT-${f.id.slice(0, 8)}-${date.toISOString().split('T')[0]}`,
      legs: [
        { accountId: intReceivable.id, amount: dailyInterest, direction: Direction.DEBIT, currency: f.currency },
        { accountId: intIncome.id, amount: dailyInterest, direction: Direction.CREDIT, currency: f.currency },
      ],
      metadata: { facilityId: f.id, outstanding: f.drawnAmount, rate, dailyInterest },
      createdBy: 'lending-engine',
    });

    const accrual = this.accruals.get(facilityId);
    accrual.accruedInterest = Math.round((accrual.accruedInterest + dailyInterest) * 100) / 100;
    accrual.entries.push({
      date: date.toISOString().split('T')[0],
      amount: dailyInterest,
      outstanding: f.drawnAmount,
      rate,
    });

    return { facilityId, dailyInterest, outstanding: f.drawnAmount, rate, totalAccrued: accrual.accruedInterest };
  }

  chargeAccruedInterest(facilityId, customerAccountId) {
    const f = this._getFacilityOrThrow(facilityId);
    const accrual = this.accruals.get(facilityId);
    if (!accrual || accrual.accruedInterest <= 0) return null;

    const amount = accrual.accruedInterest;
    const intReceivable = this.ledger.getAccountByNumber('INT-LOAN-INTEREST-RECEIVABLE');

    // DR Customer Account, CR Loan Interest Receivable (clears accrual)
    this.ledger.postTransaction({
      type: 'LOAN_INTEREST_CHARGE',
      reference: `LCHG-${f.id.slice(0, 8)}-${Date.now()}`,
      legs: [
        { accountId: customerAccountId, amount, direction: Direction.DEBIT, currency: f.currency },
        { accountId: intReceivable.id, amount, direction: Direction.CREDIT, currency: f.currency },
      ],
      metadata: { facilityId: f.id, interestCharged: amount },
      createdBy: 'lending-engine',
    });

    const charged = accrual.accruedInterest;
    accrual.accruedInterest = 0;
    accrual.entries = [];
    return { facilityId, amount: charged };
  }

  // ---- IFRS 9 ----

  migrateStage(facilityId, newStage, reason) {
    const f = this._getFacilityOrThrow(facilityId);
    if (!Object.values(IFRS9Stage).includes(newStage)) throw new Error(`Invalid IFRS 9 stage: ${newStage}`);
    const oldStage = f.ifrs9Stage;
    f.ifrs9Stage = newStage;
    f.ifrs9History.push({ stage: newStage, from: oldStage, at: new Date().toISOString(), reason });
    return { facilityId, from: oldStage, to: newStage, reason };
  }

  calculateECL(facilityId, { pd12m, pdLifetime, lgd }) {
    const f = this._getFacilityOrThrow(facilityId);
    if (pd12m == null || pdLifetime == null || lgd == null) throw new Error('pd12m, pdLifetime, and lgd are all required');

    const ead = f.drawnAmount;
    const ecl = f.ifrs9Stage === IFRS9Stage.STAGE_1
      ? Math.round(ead * pd12m * lgd * 100) / 100      // 12-month ECL
      : Math.round(ead * pdLifetime * lgd * 100) / 100; // Lifetime ECL

    const previousProvision = f.eclProvision;
    const change = ecl - previousProvision;
    f.eclProvision = ecl;

    // Post provision change to ledger if material
    if (Math.abs(change) > 0.01) {
      const provisionAccount = this.ledger.getAccountByNumber('INT-PROVISION-ECL');
      const impairmentExpense = this.ledger.getAccountByNumber('INT-IMPAIRMENT-EXPENSE');

      if (change > 0) {
        // Increase: DR Impairment Expense, CR ECL Provision
        this.ledger.postTransaction({
          type: 'ECL_PROVISION_INCREASE',
          reference: `ECL-${f.id.slice(0, 8)}`,
          legs: [
            { accountId: impairmentExpense.id, amount: Math.abs(change), direction: Direction.DEBIT, currency: f.currency },
            { accountId: provisionAccount.id, amount: Math.abs(change), direction: Direction.CREDIT, currency: f.currency },
          ],
          createdBy: 'lending-engine',
        });
      } else {
        // Release: DR ECL Provision, CR Impairment Expense
        this.ledger.postTransaction({
          type: 'ECL_PROVISION_RELEASE',
          reference: `ECLR-${f.id.slice(0, 8)}`,
          legs: [
            { accountId: provisionAccount.id, amount: Math.abs(change), direction: Direction.DEBIT, currency: f.currency },
            { accountId: impairmentExpense.id, amount: Math.abs(change), direction: Direction.CREDIT, currency: f.currency },
          ],
          createdBy: 'lending-engine',
        });
      }
    }

    return { facilityId, stage: f.ifrs9Stage, ead, ecl, previousProvision, change };
  }

  // ---- Covenants ----

  checkCovenants(facilityId, financialData) {
    const f = this._getFacilityOrThrow(facilityId);
    const results = [];
    for (const covenant of f.covenants) {
      const actual = financialData[covenant.metric];
      let breached = false;
      if (covenant.type === 'MIN' && actual < covenant.threshold) breached = true;
      if (covenant.type === 'MAX' && actual > covenant.threshold) breached = true;
      results.push({
        metric: covenant.metric, type: covenant.type,
        threshold: covenant.threshold, actual, breached,
        description: covenant.description,
      });
    }
    return { facilityId, results, anyBreach: results.some(r => r.breached) };
  }

  // ---- Queries ----

  getSchedule(facilityId) { return this.schedules.get(facilityId) || []; }
  getAccruals(facilityId) { return this.accruals.get(facilityId); }

  // ---- Internals ----

  _getFacilityOrThrow(id) {
    const f = this.facilities.get(id);
    if (!f) throw new Error(`Facility ${id} not found`);
    return f;
  }

  _generateAmortisationSchedule(f) {
    const start = new Date(f.startDate);
    const end = new Date(f.maturityDate);
    const months = Math.max(1, Math.round((end - start) / (30.44 * 24 * 60 * 60 * 1000)));
    const monthlyPrincipal = Math.round((f.committedAmount / months) * 100) / 100;
    const schedule = [];
    for (let i = 1; i <= months; i++) {
      const d = new Date(start);
      d.setMonth(d.getMonth() + i);
      schedule.push({
        installment: i,
        date: d.toISOString().split('T')[0],
        principalDue: monthlyPrincipal,
        interestDue: null,
        status: 'PENDING',
      });
    }
    this.schedules.set(f.id, schedule);
    return schedule;
  }

  _transitionFacility(f, newStatus, by) {
    if (!(FACILITY_TRANSITIONS[f.status] || []).includes(newStatus)) {
      throw new Error(`Invalid facility transition: ${f.status}→${newStatus}`);
    }
    f.status = newStatus;
    f.statusHistory.push({ status: newStatus, at: new Date().toISOString(), by });
  }
}


// ============================================================
// EXPORTS
// ============================================================

export {
  FacilityType, FacilityStatus, FACILITY_TRANSITIONS,
  RepaymentType, InterestMethod, IFRS9Stage,
  LendingEngine,
};
